import React, { useState } from "react";

/**
 * Shared building blocks for the API Documentation section of the Knowledge
 * Center. This section is the source of truth for external and partner
 * integrations, so the primitives here are deliberately plain: readable
 * tables, copyable code, and an explicit badge for where an endpoint is
 * actually reachable.
 */

export const TH = "px-3 py-2 font-medium";
export const TD = "px-3 py-2 align-top";
export const MONO = "font-mono text-xs";

export const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-mono text-xs bg-gray-100 text-gray-800 rounded px-1 py-0.5">{children}</span>
);

type Tone = "prod" | "stage" | "gap";

const TONE_CLASS: Record<Tone, string> = {
  prod: "bg-green-50 text-green-700 ring-green-200",
  stage: "bg-amber-50 text-amber-700 ring-amber-200",
  gap: "bg-red-50 text-red-700 ring-red-200",
};

/** Where an endpoint can actually be called, which is not the same as where the code is deployed. */
export const Badge: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${TONE_CLASS[tone]}`}
  >
    {children}
  </span>
);

export const Note: React.FC<{ tone?: "info" | "warn"; children: React.ReactNode }> = ({
  tone = "warn",
  children,
}) => (
  <div
    className={`rounded border-l-4 px-4 py-3 text-sm ${
      tone === "warn"
        ? "border-amber-300 bg-amber-50 text-amber-900"
        : "border-blue-300 bg-blue-50 text-blue-900"
    }`}
  >
    {children}
  </div>
);

export const Table: React.FC<{ minWidth: number; children: React.ReactNode }> = ({
  minWidth,
  children,
}) => (
  <div className="overflow-x-auto rounded border border-gray-200 bg-white">
    <table className="w-full text-sm" style={{ minWidth }}>
      {children}
    </table>
  </div>
);

export const Thead: React.FC<{ cols: string[] }> = ({ cols }) => (
  <thead className="bg-gray-100 text-left text-gray-600">
    <tr>
      {cols.map((c) => (
        <th key={c} className={TH}>
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

export const Tbody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <tbody className="divide-y divide-gray-200 text-gray-800">{children}</tbody>
);

/** Code sample with a copy button - integrators are expected to lift these verbatim. */
export const CodeBlock: React.FC<{ code: string; label?: string }> = ({ code, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => setCopied(false)
    );
  };

  return (
    <div>
      {label && <div className="mb-1 text-xs font-medium text-gray-500">{label}</div>}
      {/* The pre is the positioned ancestor, so the button sits against the code
          block itself and needs no offset when a label is present. */}
      <div className="relative">
        <button
          type="button"
          onClick={handleCopy}
          className="absolute right-2 top-2 rounded bg-gray-700 px-2 py-1 text-[11px] text-gray-100 hover:bg-gray-600"
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <pre className="overflow-x-auto rounded bg-gray-900 p-3 pr-16 text-xs leading-5 text-gray-100">
          {code}
        </pre>
      </div>
    </div>
  );
};

export const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({
  id,
  title,
  children,
}) => (
  <section id={id} className="scroll-mt-4">
    <h3 className="mb-3 text-base font-semibold text-gray-800">{title}</h3>
    <div className="space-y-4">{children}</div>
  </section>
);

/** Environment table repeated per service, differing only in the path prefix. */
export const EnvTable: React.FC<{ prodUrl: string; stageUrl: string; product: string }> = ({
  prodUrl,
  stageUrl,
  product,
}) => (
  <Table minWidth={680}>
    <Thead cols={["Environment", "Base URL", "OAuth Scope", "APIM Product"]} />
    <Tbody>
      <tr>
        <td className={`${TD} font-medium`}>Production</td>
        <td className={`${TD} ${MONO}`}>{prodUrl}</td>
        <td className={`${TD} ${MONO}`}>api://api-betm-com/.default</td>
        <td className={`${TD} ${MONO}`} rowSpan={2}>
          {product}
        </td>
      </tr>
      <tr>
        <td className={`${TD} font-medium`}>Stage</td>
        <td className={`${TD} ${MONO}`}>{stageUrl}</td>
        <td className={`${TD} ${MONO}`}>api://stage-api-betm-com/.default</td>
      </tr>
    </Tbody>
  </Table>
);
